import { useState } from "react";
import { Mail, Send, Loader2, CheckCircle, Copy, Link2, MessageSquare, Code } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API_URL = typeof window !== "undefined" ? window.location.origin : "";

export default function SendUploadLinkModal({ 
  isOpen, 
  onClose, 
  entityType,
  entityId,
  clientName,
  clientEmail: initialEmail 
}) {
  const [email, setEmail] = useState(initialEmail || "");
  const [name, setName] = useState(clientName || "");
  const [sending, setSending] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [mode, setMode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  if (!isOpen) return null;

  const generateLink = async (sendEmail = false) => {
    if (!name) {
      toast.error("Veuillez saisir le nom du client");
      return;
    }
    if (sendEmail && !email) {
      toast.error("Veuillez saisir l'email du client");
      return;
    }
    if (sendEmail && email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error("Adresse email invalide");
        return;
      }
    }

    setSending(true);

    try {
      const payload = {
        client_name: name,
        client_email: sendEmail ? email : "",
        send_email: sendEmail,
        ...(entityType === "dossier" ? { dossier_id: entityId } : { lead_id: entityId })
      };

      const response = await fetch(`${API_URL}/api/portal/send-upload-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setLinkData(data);
        if (sendEmail && data.email_sent) {
          toast.success("Email envoyé avec succès !");
        } else if (sendEmail && !data.email_sent) {
          toast.info("Lien généré. L'email n'a pas pu être envoyé, copiez le lien manuellement.");
        } else {
          toast.success("Lien de dépôt généré !");
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Erreur lors de la génération");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setSending(false);
    }
  };

  const formatExpiry = (isoDate) => {
    try {
      const d = new Date(isoDate);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      return "24h";
    }
  };

  const getHtmlTemplate = () => {
    if (!linkData) return "";
    const expiry = formatExpiry(linkData.expires_at);
    return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;background-color:#f5f5f5;">
<table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f5f5f5;padding:40px 20px;">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.1);overflow:hidden;">
<tr><td style="background:linear-gradient(135deg,#1a3a2f 0%,#2d5a47 100%);padding:40px 40px 30px 40px;text-align:center;">
<div style="width:60px;height:60px;background-color:#10b981;border-radius:12px;display:inline-block;text-align:center;line-height:60px;margin-bottom:15px;"><span style="font-size:28px;color:white;">&#9752;</span></div>
<h1 style="color:#ffffff;margin:10px 0 0 0;font-size:28px;font-weight:700;letter-spacing:1px;">RENOVA</h1>
<p style="color:#10b981;margin:5px 0 0 0;font-size:14px;font-weight:500;letter-spacing:3px;">SOLUTION</p>
</td></tr>
<tr><td style="padding:40px;">
<h2 style="color:#1a3a2f;margin:0 0 20px 0;font-size:22px;font-weight:600;">Bonjour ${name},</h2>
<p style="color:#4a5568;font-size:16px;line-height:1.7;margin:0 0 25px 0;">Dans le cadre de votre dossier de rénovation énergétique, nous vous invitons à déposer vos documents justificatifs via notre portail sécurisé.</p>
<table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f0fdf4;border-radius:12px;margin-bottom:25px;">
<tr><td style="padding:20px;"><table cellpadding="0" cellspacing="0"><tr>
<td style="padding-right:15px;"><div style="width:40px;height:40px;background-color:#10b981;border-radius:50%;text-align:center;line-height:40px;"><span style="color:white;font-size:18px;">&#128274;</span></div></td>
<td><p style="color:#166534;margin:0;font-size:14px;font-weight:600;">Transfert 100% sécurisé</p><p style="color:#4a5568;margin:5px 0 0 0;font-size:13px;">Vos documents sont chiffrés et protégés</p></td>
</tr></table></td></tr></table>
<table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:10px 0 30px 0;">
<a href="${linkData.upload_url}" style="display:inline-block;background:linear-gradient(135deg,#10b981 0%,#059669 100%);color:#ffffff;text-decoration:none;padding:16px 40px;border-radius:8px;font-size:16px;font-weight:600;box-shadow:0 4px 15px rgba(16,185,129,0.3);">Déposer mes documents</a>
</td></tr></table>
<p style="color:#9ca3af;font-size:13px;text-align:center;margin:0 0 20px 0;">&#9200; Ce lien expire le <strong>${expiry}</strong></p>
<table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f8fafc;border-radius:8px;margin-top:20px;">
<tr><td style="padding:15px 20px;"><p style="color:#64748b;font-size:13px;margin:0;"><strong>Formats acceptés :</strong> PDF, JPG, PNG, DOC, DOCX<br><strong>Taille max :</strong> 10 Mo par fichier</p></td></tr></table>
</td></tr>
<tr><td style="background-color:#f8fafc;padding:25px 40px;border-top:1px solid #e2e8f0;">
<p style="color:#64748b;font-size:12px;margin:0;text-align:center;line-height:1.6;">Cet email vous a été envoyé par <strong>RENOVA Solution</strong><br>Spécialiste de la rénovation énergétique</p>
</td></tr></table></td></tr></table></body></html>`;
  };

  const handleCopyHtml = async () => {
    try {
      const htmlContent = getHtmlTemplate();
      // Copy as rich HTML so it pastes as formatted in email clients
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob, 'text/plain': new Blob([htmlContent], { type: 'text/plain' }) });
      await navigator.clipboard.write([clipboardItem]);
      setCopied(true);
      toast.success("Template HTML copié !");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      // Fallback: copy as plain HTML text
      try {
        await navigator.clipboard.writeText(getHtmlTemplate());
        setCopied(true);
        toast.success("Template HTML copié !");
        setTimeout(() => setCopied(false), 3000);
      } catch (err) {
        const textarea = document.createElement('textarea');
        textarea.value = getHtmlTemplate();
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        toast.success("Template HTML copié !");
        setTimeout(() => setCopied(false), 3000);
      }
    }
  };

  const handleCopyLink = async () => {
    if (!linkData) return;
    try {
      await navigator.clipboard.writeText(linkData.upload_url);
      setCopiedLink(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = linkData.upload_url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedLink(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopiedLink(false), 3000);
    }
  };

  const handleClose = () => {
    setEmail(initialEmail || "");
    setName(clientName || "");
    setSending(false);
    setLinkData(null);
    setMode(null);
    setCopied(false);
    setCopiedLink(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div className="bg-card rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a3a2f] to-[#2d5a47] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Lien de dépôt client</h2>
              <p className="text-emerald-300 text-sm">Générez un lien sécurisé pour {name || "le client"}</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Name + mode choice */}
          {!linkData && !mode && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nom du client *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nom du client"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                  data-testid="upload-link-name-input"
                />
              </div>

              <p className="text-sm text-slate-500 mb-4">Choisissez comment partager le lien :</p>

              <div className="grid grid-cols-1 gap-3">
                {/* Option 1: Copy HTML template */}
                <button
                  onClick={() => { setMode("copy"); generateLink(false); }}
                  disabled={sending || !name}
                  className="flex items-center gap-4 p-4 border-2 border-slate-200 rounded-xl hover:border-emerald-400 hover:bg-emerald-500/10 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                  data-testid="upload-link-copy-mode-btn"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors shrink-0">
                    <MessageSquare className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Copier le template</p>
                    <p className="text-sm text-slate-500">Générer un template HTML prêt à coller dans votre messagerie</p>
                  </div>
                </button>

                {/* Option 2: Send email */}
                <button
                  onClick={() => setMode("email")}
                  disabled={sending || !name}
                  className="flex items-center gap-4 p-4 border-2 border-slate-200 rounded-xl hover:border-blue-400 hover:bg-blue-500/10 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                  data-testid="upload-link-email-mode-btn"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Envoyer par email</p>
                    <p className="text-sm text-slate-500">Envoyer un email automatique au client</p>
                  </div>
                </button>
              </div>
            </>
          )}

          {/* Mode: Email - show email input */}
          {mode === "email" && !linkData && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email du client *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@email.com"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                  data-testid="upload-link-email-input"
                  autoFocus
                />
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-700">
                  Le client recevra un email professionnel aux couleurs RENOVA avec un bouton pour déposer ses documents. Le lien expire dans 24h.
                </p>
              </div>
            </>
          )}

          {/* Loading state */}
          {sending && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              <span className="ml-3 text-slate-600">Génération du lien...</span>
            </div>
          )}

          {/* Link generated - Copy mode: show HTML template */}
          {linkData && mode === "copy" && !sending && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="font-semibold text-emerald-700">Lien généré avec succès !</span>
              </div>

              {/* Link preview + copy */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-slate-600 truncate flex-1 font-mono">{linkData.upload_url}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="shrink-0"
                    data-testid="copy-link-btn"
                  >
                    {copiedLink ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-1.5">{copiedLink ? "Copié !" : "Copier le lien"}</span>
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Expire le {formatExpiry(linkData.expires_at)}
                </p>
              </div>

              {/* HTML email preview with copy button */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                    <Code className="w-4 h-4" />
                    Template HTML à copier :
                  </label>
                  <Button
                    variant={copied ? "default" : "outline"}
                    size="sm"
                    onClick={handleCopyHtml}
                    className={copied ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                    data-testid="copy-html-btn"
                  >
                    {copied ? <CheckCircle className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                    {copied ? "Copié !" : "Copier le template"}
                  </Button>
                </div>
                {/* Visual preview */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-[#1a3a2f] to-[#2d5a47] p-5 text-center">
                    <div className="inline-block w-11 h-11 bg-emerald-500 rounded-xl mb-2" style={{lineHeight:'44px'}}>
                      <span className="text-lg text-white">&#9752;</span>
                    </div>
                    <h3 className="text-white font-bold text-lg">RENOVA</h3>
                    <p className="text-emerald-400 text-xs tracking-widest font-medium">SOLUTION</p>
                  </div>
                  <div className="p-5 bg-card">
                    <h4 className="text-base font-semibold text-slate-800 mb-2">Bonjour {name},</h4>
                    <p className="text-slate-600 text-sm mb-3 leading-relaxed">
                      Dans le cadre de votre dossier de rénovation énergétique, nous vous invitons à déposer vos documents justificatifs via notre portail sécurisé.
                    </p>
                    <div className="bg-emerald-500/10 rounded-lg p-2.5 mb-3 flex items-center gap-2.5">
                      <div className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs shrink-0">&#128274;</div>
                      <div>
                        <p className="text-emerald-800 text-xs font-semibold">Transfert 100% sécurisé</p>
                        <p className="text-slate-500 text-xs">Vos documents sont chiffrés et protégés</p>
                      </div>
                    </div>
                    <div className="text-center mb-3">
                      <a 
                        href={linkData.upload_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block bg-emerald-500 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-emerald-600 transition-colors"
                      >
                        Déposer mes documents
                      </a>
                    </div>
                    <p className="text-xs text-slate-400 text-center">
                      Ce lien expire le <strong>{formatExpiry(linkData.expires_at)}</strong>
                    </p>
                    <div className="mt-3 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-400">
                        <strong>Formats acceptés :</strong> PDF, JPG, PNG, DOC, DOCX | <strong>Max :</strong> 10 Mo
                      </p>
                    </div>
                  </div>
                  <div className="bg-slate-50 p-2.5 border-t text-center">
                    <p className="text-xs text-slate-400">RENOVA Solution - Spécialiste de la rénovation énergétique</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Link generated - Email mode */}
          {linkData && mode === "email" && !sending && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {linkData.email_sent ? "Email envoyé !" : "Lien généré !"}
              </h3>
              <p className="text-slate-600 mb-4">
                {linkData.email_sent 
                  ? "Le client recevra un email avec un lien sécurisé pour déposer ses documents."
                  : "L'email n'a pas pu être envoyé. Vous pouvez copier le lien ci-dessous."
                }
              </p>
              <div className="bg-slate-50 border rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-slate-600 truncate flex-1 font-mono">{linkData.upload_url}</p>
                  <Button variant="outline" size="sm" onClick={handleCopyLink} data-testid="copy-link-email-btn">
                    {copiedLink ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-1.5">{copiedLink ? "Copié !" : "Copier"}</span>
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Expiration : {formatExpiry(linkData.expires_at)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3">
          {mode === "email" && !linkData && !sending && (
            <Button variant="ghost" onClick={() => setMode(null)} className="text-slate-500">
              Retour
            </Button>
          )}
          
          {mode === "email" && !linkData && !sending && (
            <Button
              onClick={() => generateLink(true)}
              disabled={sending || !email || !name}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-email-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Envoyer l'email
            </Button>
          )}

          <Button variant="outline" onClick={handleClose}>
            {linkData ? "Fermer" : "Annuler"}
          </Button>
        </div>
      </div>
    </div>
  );
}
